import React, { useMemo } from 'react';

import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { HeadingLevel1 } from '@/components/shared/headline/heading-level-1';
import { Box } from '@/components/shared/layout/box';
import { Card } from '@/components/shared/layout/card';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { Pagination } from '@/components/shared/navigation/pagination';
import { Tabs } from '@/components/shared/navigation/tabs';
import { IButton } from '@/types/shared';

export interface OfferCategory {
  name: string;
  url: string;
  active: boolean;
}
export interface OfferListProps {
  children?: React.ReactNode;
  title: string;
  description: string;
  buttons?: IButton[];
  items: {
    image: ImageProps;
    title: string;
    url: string;
    detail: string;
    button?: IButton;
  }[];
  category: OfferCategory[];
  pagination: {
    current: number;
    total: number;
  };
  onClickPage?: (page: number) => void;
}

const OfferListSection: React.FC<OfferListProps> = ({
  title,
  description,
  buttons,
  category,
  pagination,
  onClickPage,
  items = [],
}) => {
  const totalItemsCount = useMemo(() => {
    return items.length * pagination.total;
  }, [items.length, pagination.total]);

  return (
    <Section backgroundColor="gradient-gray-light" test="section_specialoffer">
      <HeadingLevel1
        title={title}
        description={description}
        buttons={buttons}
      />
      <Tabs category={category} />
      <Section as="div" size="xl" test="offers">
        <Container>
          {items.length === 0 ? (
            <Grid
              alignItems="center"
              justifyContent="center"
              rowGap="2"
              css={{
                textAlign: 'center',
                py: '$space-24',
                '@sm': {
                  py: '$space-28',
                },
              }}
            >
              <Box>
                <Icon icon="face-sad" size="lg" color="icon-gray-light" />
              </Box>
              <Box>
                <Text as="p" variant="text-gray">
                  ขออภัย ยังไม่มีข้อเสนอพิเศษประเภทนี้
                </Text>
              </Box>
            </Grid>
          ) : (
            <Grid
              templateColumns={{ '@md': '2' }}
              alignItems="start"
              columnGap={{ '@initial': '6', '@md': '8', '@lg': '16' }}
              rowGap={{ '@initial': '8', '@sm': '10', '@lg': '16' }}
            >
              {items.map((offer, index) => (
                <Card
                  key={index}
                  image={{
                    ...offer?.image,
                    priority: index <= 1,
                  }}
                  title={offer.title}
                  button={offer.button}
                  tracking={{
                    dataTrack: 'offer-list',
                    dataTrackText: offer.button?.label,
                    dataTrackUrl: offer.button?.url,
                    dataTrackValue: offer.title,
                  }}
                />
              ))}
            </Grid>
          )}
        </Container>
        {pagination.total > 1 && (
          <Pagination
            pagination={pagination}
            onClickPage={onClickPage}
            data-item={totalItemsCount}
          />
        )}
      </Section>
    </Section>
  );
};

OfferListSection.displayName = 'OfferListSection';
export default OfferListSection;
